import { useState } from 'react';
import type { PropsWithChildren } from 'react';
import { api } from '../api';
import { Btn, Card, CardTitle, Field, Modal, Notice } from '../components/ui';
import { usePortal } from '../App';

// Piezas del espacio de empresa compartidas entre DriverView y OfficeView:
// onboarding (registrar o unirse), modal de código de un solo uso y salida.

/** El código de empresa del backend son 8 caracteres sin 0/O/1/I/L; se aceptan
    minúsculas y se descarta lo demás al teclear o pegar (guiones incluidos). */
const sanitizeCompanyCode = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);

/** Tarjeta de entrada para quien aún no pertenece a una empresa: registrar la
    propia (queda por autorizar) o unirse con el código que da la oficina. */
export function CompanyOnboarding() {
  const [registering, setRegistering] = useState(false);
  const [joining, setJoining] = useState(false);
  const [created, setCreated] = useState<{ name: string; code: string } | null>(null);
  return <>
    <Card>
      <CardTitle detail="Espacio privado de Cargados.mx: lugares, notas y viajes que solo ven los miembros de tu empresa.">Tu empresa</CardTitle>
      <p className="muted small u-m0">Si despachas para tu propio equipo, registra tu empresa; si tu empresa ya está registrada, pide su código de invitación a la oficina.</p>
      <div className="actions">
        <Btn onClick={() => setRegistering(true)}>Registrar mi empresa</Btn>
        <Btn tone="secondary" onClick={() => setJoining(true)}>Unirme con código</Btn>
      </div>
    </Card>
    {registering && <RegisterCompanyModal onClose={() => setRegistering(false)} onCreated={(name, code) => setCreated({ name, code })} />}
    {joining && <JoinCompanyModal onClose={() => setJoining(false)} />}
    {created && <CodeModal title="Tu empresa quedó registrada" code={created.code} onClose={() => setCreated(null)}>
      <p className="muted small u-m0">
        Código de invitación de <b>{created.name}</b>: compártelo con tus choferes — <b>se muestra una sola vez</b> y no se puede recuperar.
        Tu empresa queda pendiente de autorización del administrador del directorio.
      </p>
    </CodeModal>}
  </>;
}

/** Modal de alta de empresa: nombre → registerCompany → el código se muestra
    una sola vez en el CodeModal que abre el padre con `onCreated`. */
function RegisterCompanyModal({ onClose, onCreated }: { onClose(): void; onCreated(name: string, code: string): void }) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const [name, setName] = useState('');
  const submit = async () => {
    let code = '';
    const ok = await mutate(async () => {
      const result = await api<{ id: string; code: string }>('registerCompany', { name: name.trim() }, token);
      code = result.code;
    }, 'Empresa registrada — queda pendiente de autorización del administrador del directorio.');
    if (ok) { if (code) onCreated(name.trim(), code); onClose(); }
  };
  return <Modal title="Registrar mi empresa" onClose={onClose}>
    <Notice tone="info">Quedará «por autorizar» hasta que el administrador del directorio la apruebe. Al registrarla te conviertes en su oficina.</Notice>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <Field label="Nombre de la empresa" value={name} onChange={e => setName(e.target.value)} placeholder="Ejemplo: Transportes Demo" maxLength={80} autoCapitalize="words" autoFocus />
    <div className="actions end">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={name.trim().length < 2} onClick={() => void submit()}>Registrar empresa</Btn>
    </div>
  </Modal>;
}

/** Modal de unión con código: 8 caracteres. Los errores (código inválido,
    empresa pendiente/suspendida, límite de intentos) se ven dentro del modal. */
function JoinCompanyModal({ onClose }: { onClose(): void }) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const [code, setCode] = useState('');
  const submit = async () => {
    const ok = await mutate(() => api('joinCompany', { code }, token), 'Te uniste a la empresa.');
    if (ok) onClose();
  };
  return <Modal title="Unirme con código" onClose={onClose}>
    <Notice tone="info">Pide el código de invitación a la oficina de tu empresa. Al unirte empezarás a ver sus lugares, notas y viajes privados.</Notice>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <Field label="Código de invitación" value={code} onChange={e => setCode(sanitizeCompanyCode(e.target.value))}
      placeholder="8 caracteres" hint="Letras y números, sin espacios ni guiones." autoCapitalize="characters" autoComplete="off" autoFocus />
    <div className="actions end">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={code.length !== 8} onClick={() => void submit()}>Unirme a la empresa</Btn>
    </div>
  </Modal>;
}

/** Modal con el código de invitación en grande + copiar: se muestra una sola
    vez (el servidor solo guarda su hash). Lo usan el alta de empresa y la
    rotación de código en oficina y administración. */
export function CodeModal({ title = 'Código de invitación', code, children, onClose }: PropsWithChildren<{ title?: string; code: string; onClose(): void }>) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };
  return <Modal title={title} onClose={onClose}>
    {children}
    <div className="code-chip">{code}</div>
    <Btn onClick={copy}>{copied ? 'Copiado ✓' : 'Copiar código'}</Btn>
  </Modal>;
}

/** Salida de la empresa (leaveCompany): pierde los datos privados pero no su
    acceso al directorio. `lastOffice` refuerza el aviso cuando nadie quedaría
    administrando — el backend lo permite, así que solo se advierte. */
export function LeaveCompanyButton({ companyName, lastOffice }: { companyName: string; lastOffice?: boolean }) {
  const { token, busy, mutate } = usePortal();
  const leave = () => {
    const extra = lastOffice ? ' Además eres la única persona en la oficina: la empresa quedaría sin quien la administre.' : '';
    if (!confirm(`¿Salir de ${companyName || 'tu empresa'}? Perderás el acceso a sus lugares, notas y viajes privados; tu acceso al directorio no cambia.${extra}`)) return;
    void mutate(() => api('leaveCompany', {}, token), 'Saliste de la empresa.');
  };
  return <Btn small tone="danger" busy={busy} onClick={leave}>Salir de la empresa</Btn>;
}
